import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';

const AppleLoginButton = ({ role, onSuccess }) => {
    const { appleLogin } = useAuth();
    const { showError } = useNotification();
    const [isNative, setIsNative] = useState(false);
    const [showEmailPrompt, setShowEmailPrompt] = useState(false);
    const [manualEmail, setManualEmail] = useState('');
    const pendingAppleRef = useRef(null);

    useEffect(() => {
        setIsNative(typeof window !== 'undefined' && window.Capacitor?.isNativePlatform());
    }, []);

    const performAppleLogin = async () => {
        try {
            const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
            const result = await SignInWithApple.authorize({
                clientId: isNative ? 'com.vivara.healthcare' : 'com.vivara.healthcare.client',
                redirectURI: 'https://drvivara.cloud/auth/apple/callback',
                scopes: 'email name',
                nonce: '1234567890' // A standard nonce is recommended for Apple login validation
            });

            if (result.response && result.response.identityToken) {
                const firstName = result.response.givenName || '';
                const lastName = result.response.familyName || '';
                const userName = `${firstName} ${lastName}`.trim() || null;
                const authResult = await appleLogin({
                    identityToken: result.response.identityToken,
                    email: result.response.email,
                    name: userName,
                    role: role
                });
                if (authResult.success) {
                    if (onSuccess) onSuccess(authResult.user);
                } else if (authResult.message && authResult.message.startsWith('NO_EMAIL:')) {
                    pendingAppleRef.current = {
                        identityToken: result.response.identityToken,
                        name: userName,
                    };
                    setShowEmailPrompt(true);
                } else {
                    showError(authResult.message || 'Apple Sign-In failed');
                }
            }
        } catch (error) {
            console.error('Apple Sign-In error:', error);
            if (error.message !== 'User cancelled') {
                showError(`Apple Sign-In failed: ${error.message || 'Please try again.'}`);
            }
        }
    };

    const handleEmailSubmit = async () => {
        const email = manualEmail.trim();
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            showError('Please enter a valid email address');
            return;
        }
        setShowEmailPrompt(false);
        setManualEmail('');
        const pending = pendingAppleRef.current;
        if (!pending) return;
        const authResult = await appleLogin({
            identityToken: pending.identityToken,
            email: email,
            name: pending.name,
            role: role
        });
        if (authResult.success) {
            if (onSuccess) onSuccess(authResult.user);
        } else {
            showError(authResult.message || 'Apple Sign-In failed');
        }
        pendingAppleRef.current = null;
    };

    if (!isNative) return null;

    return (
        <>
            <button
                onClick={performAppleLogin}
                className="w-full flex items-center justify-center gap-2 bg-black text-white h-[50px] rounded-lg font-semibold hover:bg-gray-900 transition-colors border border-gray-800"
                style={{ fontWeight: 600, fontSize: '17px' }}
            >
                <svg className="w-5 h-5 mb-0.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.057 10.78c.045 1.832 1.587 2.455 1.625 2.472-.015.045-.255.875-.845 1.74-.51.745-1.04 1.485-1.875 1.5-.82.015-1.085-.485-2.025-.485-.925 0-1.22.47-1.99.495-.805.015-1.425-.805-1.935-1.545-1.045-1.52-1.845-4.295-.77-6.175.535-.935 1.495-1.525 2.535-1.54.78-.015 1.52.53 2 .53.48 0 1.385-.665 2.32-.57.39.015 1.49.155 2.195 1.185-.06.03-.02.045-.02.045zM15.482 4.145c.42-.51.71-1.22.63-1.935-.615.025-1.36.41-1.81.935-.4.46-.75 1.185-.655 1.885.69.055 1.41-.375 1.835-.885z" />
                </svg>
                <span className="mb-0.5">Sign in with Apple</span>
            </button>

            {showEmailPrompt && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Enter Your Email</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Apple didn't provide your email. Enter it to continue.
                        </p>
                        <input
                            type="email"
                            value={manualEmail}
                            onChange={(e) => setManualEmail(e.target.value)}
                            placeholder="email@example.com"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-base bg-white focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-gray-500 mb-4"
                            style={{ color: '#111827' }}
                            autoFocus
                        />
                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowEmailPrompt(false); setManualEmail(''); pendingAppleRef.current = null; }}
                                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleEmailSubmit}
                                className="flex-1 py-2.5 rounded-lg bg-black text-white font-medium hover:bg-gray-900 transition-colors"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AppleLoginButton;
